// Text catalog for the legal/policy pages recreated on the old WordPress URLs
// after the migration (so they keep resolving 200 and preserve SEO):
//   - cookiepolitik  -> Cookie- og privatlivspolitik
//   - persondata     -> Persondatapolitik
//
// The text is reproduced faithfully from the live misupport.dk pages (legal
// content — do not invent or drop clauses). The blocks render via
// src/pages/LegalPage.tsx as a simple titled prose article. Editable: adjust
// the strings below; the structure is a plain ordered list of prose blocks.

import { type Locale } from "../config";
import { type LegalStrings } from "@/pages/LegalPage";

// ---------------------------------------------------------------------------
// Cookie- og privatlivspolitik
// ---------------------------------------------------------------------------
export const cookiepolitikStrings: Record<Locale, LegalStrings> = {
  da: {
    meta: {
      title: "Cookie- og privatlivspolitik - MI Support",
      description:
        "Læs hvordan MI Support A/S indsamler, behandler og opbevarer dine personoplysninger samt anvender cookies, når du besøger vores website.",
    },
    title: "Cookie- og privatlivspolitik",
    intro:
      "Når du besøger MI Supports website, indsamles der oplysninger om dig, som bruges til at tilpasse og forbedre vores indhold og til at øge værdien af de annoncer, der vises på siden. Hvis du ikke ønsker, at der indsamles oplysninger, bør du slette dine cookies (se vejledning) og undlade videre brug af websitet.",
    blocks: [
      { type: "heading", text: "Introduktion" },
      {
        type: "paragraph",
        text: "Nedenfor har vi uddybet, hvilke informationer der indsamles, deres formål og hvilke tredjeparter, der har adgang til dem.",
      },
      { type: "paragraph", text: "Kontakt os hvis du har spørgsmål." },
      { type: "heading", text: "Cookies" },
      {
        type: "paragraph",
        text: 'Websitet anvender "cookies", der er en tekstfil, som gemmes på din computer, mobil el. tilsvarende med det formål at genkende den, huske indstillinger, udføre statistik og målrette annoncer. Cookies kan ikke indeholde skadelig kode som f.eks. virus.',
      },
      {
        type: "paragraph",
        text: "Det er muligt at slette eller blokere for cookies. Se vejledning: http://minecookies.org/cookiehandtering",
      },
      {
        type: "paragraph",
        text: "Hvis du sletter eller blokerer cookies, vil annoncer kunne blive mindre relevante for dig og optræde hyppigere. Du kan desuden risikere at websitet ikke fungerer optimalt samt at der er indhold, du ikke kan få adgang til.",
      },
      { type: "heading", text: "Personoplysninger" },
      { type: "heading", text: "Generelt" },
      {
        type: "paragraph",
        text: "Personoplysninger er alle slags informationer, der i et eller andet omfang kan henføres til dig. Når du benytter vores website, indsamler og behandler vi en række sådanne informationer. Det sker f.eks. ved alm. tilgang af indhold, hvis du tilmelder dig vores nyhedsbrev, deltager i konkurrencer eller undersøgelser, registrerer dig som bruger eller abonnent, øvrig brug af services eller foretager køb via websitet.",
      },
      {
        type: "paragraph",
        text: "Vi indsamler og behandler typisk følgende typer af oplysninger: Et unikt ID og tekniske oplysninger om din computer, tablet eller mobiltelefon, dit IP-nummer, geografisk placering, samt hvilke sider du klikker på (interesser). I det omfang du selv giver eksplicit samtykke hertil og selv indtaster informationerne behandles desuden: Navn, telefonnummer, e-mail, adresse og betalingsoplysninger. Det vil typisk være i forbindelse med oprettelse af login eller ved køb.",
      },
      { type: "heading", text: "Sikkerhed" },
      {
        type: "paragraph",
        text: "Vi behandler dine personoplysninger sikkert og fortroligt i overensstemmelse med gældende lovgivning, herunder persondataforordningen og databeskyttelsesloven.",
      },
      {
        type: "paragraph",
        text: "Dine oplysninger vil alene blive anvendt til det formål, de er indsamlet til, og vil blive slettet, når dette formål er opfyldt eller ikke længere relevant.",
      },
      {
        type: "paragraph",
        text: "Vi har truffet tekniske og organisatoriske foranstaltninger mod, at dine oplysninger hændeligt eller ulovligt bliver slettet, offentliggjort, fortabt, forringet eller kommer til uvedkommendes kendskab, misbruges eller i øvrigt behandles i strid med lovgivningen.",
      },
      { type: "heading", text: "Formål" },
      {
        type: "paragraph",
        text: "Oplysningerne bruges til at identificere dig som bruger og vise dig de annoncer, som vil have størst sandsynlighed for at være relevante for dig, at registrere dine køb og betalinger, samt at kunne levere de services, du har efterspurgt, som f.eks. at fremsende et nyhedsbrev. Herudover anvender vi oplysningerne til at optimere vores services og indhold.",
      },
      { type: "heading", text: "Periode for opbevaring" },
      {
        type: "paragraph",
        text: "Oplysningerne opbevares i det tidsrum, der er tilladt i henhold til lovgivningen, og vi sletter dem, når de ikke længere er nødvendige. Perioden afhænger af karakteren af oplysningen og baggrunden for opbevaring. Det er derfor ikke muligt at angive en generel tidsramme for, hvornår informationer slettes.",
      },
      { type: "heading", text: "Videregivelse af oplysninger" },
      {
        type: "paragraph",
        text: 'Data om din brug af websitet, hvilke annoncer, du modtager og evt. klikker på, geografisk placering, køn og alderssegment m.v. videregives til tredjeparter i det omfang disse oplysninger er kendt. Du kan se hvilke tredjeparter, der er tale om, i afsnittet om "Cookies" ovenfor. Oplysningerne anvendes til målretning af annoncering.',
      },
      {
        type: "paragraph",
        text: "Vi benytter herudover en række tredjeparter til opbevaring og behandling af data. Disse behandler udelukkende oplysninger på vores vegne og må ikke anvende dem til egne formål.",
      },
      {
        type: "paragraph",
        text: "Videregivelse af personoplysninger som navn og e-mail m.v. vil kun ske, hvis du giver samtykke til det. Vi anvender kun databehandlere i EU eller i lande, der kan give dine oplysninger en tilstrækkelig beskyttelse.",
      },
      { type: "heading", text: "Indsigt og klager" },
      {
        type: "paragraph",
        text: "Du har ret til at få oplyst, hvilke personoplysninger, vi behandler om dig i et almindeligt format (dataportabilitet). Du kan desuden til enhver tid gøre indsigelse mod, at oplysninger anvendes. Du kan også tilbagekalde dit samtykke til, at der bliver behandlet oplysninger om dig. Hvis de oplysninger, der behandles om dig, er forkerte har du ret til at de bliver rettet eller slettet. Henvendelse herom kan ske til: info@misupport.dk.",
      },
      {
        type: "paragraph",
        text: "Hvis du vil klage over vores behandling af dine personoplysninger, har du også mulighed for at tage kontakt til Datatilsynet.",
      },
      {
        type: "paragraph",
        text: "Ønsker du ikke længere, at vi skal behandle dine personoplysninger, eller at vi skal begrænse behandlingen af dine personoplysninger, kan du også sende os en anmodning herom til ovennævnte e-mailadresse.",
      },
      { type: "heading", text: "Udgiver" },
      { type: "paragraph", text: "Websitet ejes og publiceres af:" },
      {
        type: "list",
        items: [
          "MI Support A/S",
          "Korskildelund 4, 1.",
          "2670 Greve",
          "Telefon: +45 5555 12 12",
          "Email: info@misupport.dk",
        ],
      },
    ],
  },

  en: {
    meta: {
      title: "Cookie and privacy policy - MI Support",
      description:
        "Read how MI Support A/S collects, processes and stores your personal data and uses cookies when you visit our website.",
    },
    title: "Cookie and privacy policy",
    intro:
      "When you visit MI Support's website, information about you is collected that is used to customize and improve our content and to increase the value of the ads displayed on the site. If you do not want information to be collected, you should delete your cookies (see instructions) and refrain from further use of the website.",
    blocks: [
      { type: "heading", text: "Getting started" },
      {
        type: "paragraph",
        text: "Below we have elaborated on what information is collected, its purpose and which third parties have access to it.",
      },
      { type: "paragraph", text: "Contact us if you have any questions." },
      { type: "heading", text: "Cookies" },
      {
        type: "paragraph",
        text: 'The website uses "cookies", which is a text file that is stored on your computer, mobile or similar for the purpose of recognizing it, remembering settings, performing statistics and targeting ads. Cookies cannot contain malicious code such as viruses.',
      },
      {
        type: "paragraph",
        text: "It is possible to delete or block cookies. See instructions: http://minecookies.org/cookiehandtering",
      },
      {
        type: "paragraph",
        text: "If you delete or block cookies, ads may become less relevant to you and appear more frequently. You may also risk that the website does not function optimally and that there is content that you cannot access.",
      },
      { type: "heading", text: "Personal data" },
      { type: "heading", text: "In general" },
      {
        type: "paragraph",
        text: "Personal data is any kind of information that can be attributed to you to one extent or another. When you use our website, we collect and process a number of such information. This happens, for example, when you access content, sign up for our newsletter, participate in competitions or surveys, register as a user or subscriber, use other services or make purchases via the website.",
      },
      {
        type: "paragraph",
        text: "We typically collect and process the following types of information: A unique ID and technical information about your computer, tablet or mobile phone, your IP number, geographical location, and which pages you click on (interests). To the extent that you explicitly consent to this and enter the information yourself, we also process: Name, phone number, e-mail, address and payment information. This will typically be in connection with creating a login or making a purchase.",
      },
      { type: "heading", text: "Safety and security" },
      {
        type: "paragraph",
        text: "We process your personal data securely and confidentially in accordance with applicable legislation, including the General Data Protection Regulation and the Data Protection Act.",
      },
      {
        type: "paragraph",
        text: "Your data will only be used for the purpose for which it was collected and will be deleted when that purpose is fulfilled or no longer relevant.",
      },
      {
        type: "paragraph",
        text: "We have taken technical and organizational measures to prevent your data from being accidentally or illegally deleted, published, lost, impaired or disclosed to unauthorized persons, misused or otherwise processed in violation of the law.",
      },
      { type: "heading", text: "Purpose" },
      {
        type: "paragraph",
        text: "The information is used to identify you as a user and show you the ads that are most likely to be relevant to you, to register your purchases and payments, and to provide the services you have requested, such as sending a newsletter. In addition, we use the information to optimize our services and content.",
      },
      { type: "heading", text: "Storage period" },
      {
        type: "paragraph",
        text: "The data is stored for the period permitted by law and we delete it when it is no longer necessary. The period depends on the nature of the data and the reason for storage. It is therefore not possible to give a general time frame for when information is deleted.",
      },
      { type: "heading", text: "Disclosure of information" },
      {
        type: "paragraph",
        text: 'Data about your use of the website, which ads you receive and possibly click on, geographic location, gender and age segment, etc. is disclosed to third parties to the extent that this information is known. You can see which third parties are involved in the section on "Cookies" above. The information is used to target advertising.',
      },
      {
        type: "paragraph",
        text: "We also use a number of third parties to store and process data. They only process data on our behalf and may not use it for their own purposes.",
      },
      {
        type: "paragraph",
        text: "Disclosure of personal data such as name and e-mail etc. will only take place if you consent to it. We only use data processors in the EU or in countries that can provide adequate protection for your data.",
      },
      { type: "heading", text: "Insights and complaints" },
      {
        type: "paragraph",
        text: "You have the right to know what personal data we process about you in a common format (data portability). You can also object to the use of your data at any time. You can also withdraw your consent to the processing of your data. If the data processed about you is incorrect, you have the right to have it corrected or deleted. Inquiries about this can be made to: info@misupport.dk.",
      },
      {
        type: "paragraph",
        text: "If you want to complain about our processing of your personal data, you also have the option to contact the Danish Data Protection Agency (Datatilsynet).",
      },
      {
        type: "paragraph",
        text: "If you no longer wish us to process your personal data or if you wish us to restrict the processing of your personal data, you can also send us a request to the above-mentioned e-mail address.",
      },
      { type: "heading", text: "Publisher" },
      { type: "paragraph", text: "The website is owned and published by:" },
      {
        type: "list",
        items: [
          "MI Support A/S",
          "Korskildelund 4, 1.",
          "2670 Greve",
          "Phone: +45 5555 12 12",
          "Email: info@misupport.dk",
        ],
      },
    ],
  },

  de: {
    meta: {
      title: "Cookie- und Datenschutzrichtlinie - MI Support",
      description:
        "Lesen Sie, wie MI Support A/S Ihre personenbezogenen Daten erhebt, verarbeitet und speichert und Cookies verwendet, wenn Sie unsere Website besuchen.",
    },
    title: "Cookie- und Datenschutzrichtlinie",
    intro:
      "Wenn Sie die Website von MI Support besuchen, werden Informationen über Sie gesammelt, die dazu verwendet werden, unsere Inhalte zu personalisieren und zu verbessern und den Wert der auf der Seite angezeigten Werbung zu steigern. Wenn Sie nicht möchten, dass Informationen gesammelt werden, sollten Sie Ihre Cookies löschen (siehe Anleitung) und von der weiteren Nutzung der Website absehen.",
    blocks: [
      { type: "heading", text: "Einführung" },
      {
        type: "paragraph",
        text: "Nachfolgend haben wir erläutert, welche Informationen gesammelt werden, zu welchem Zweck und welche Dritten Zugang zu diesen Informationen haben.",
      },
      { type: "paragraph", text: "Kontaktieren Sie uns, wenn Sie Fragen haben." },
      { type: "heading", text: "Cookies" },
      {
        type: "paragraph",
        text: 'Die Website verwendet "Cookies", d.h. eine Textdatei, die auf Ihrem Computer, Ihrem Mobiltelefon oder einem ähnlichen Gerät gespeichert wird, um dieses zu erkennen, Einstellungen zu speichern, Statistiken zu erstellen und Werbung gezielt auszurichten. Cookies können keinen schädlichen Code wie z.B. Viren enthalten.',
      },
      {
        type: "paragraph",
        text: "Es ist möglich, Cookies zu löschen oder zu blockieren. Siehe Anleitung: http://minecookies.org/cookiehandtering",
      },
      {
        type: "paragraph",
        text: "Wenn Sie Cookies löschen oder blockieren, kann es sein, dass die Werbung für Sie weniger relevant wird und häufiger erscheint. Außerdem besteht das Risiko, dass die Website nicht optimal funktioniert und dass es Inhalte gibt, auf die Sie nicht zugreifen können.",
      },
      { type: "heading", text: "Persönliche Daten" },
      { type: "heading", text: "Im Allgemeinen" },
      {
        type: "paragraph",
        text: "Personenbezogene Daten sind alle Informationen, die Ihnen in irgendeiner Form zugeordnet werden können. Wenn Sie unsere Website nutzen, erheben und verarbeiten wir eine Reihe solcher Informationen. Dies geschieht z.B. beim allgemeinen Zugriff auf Inhalte, wenn Sie sich für unseren Newsletter anmelden, an Wettbewerben oder Umfragen teilnehmen, sich als Nutzer oder Abonnent registrieren, andere Dienste nutzen oder über die Website Käufe tätigen.",
      },
      {
        type: "paragraph",
        text: "Wir erheben und verarbeiten in der Regel folgende Arten von Informationen: Eine eindeutige ID und technische Informationen über Ihren Computer, Ihr Tablet oder Ihr Mobiltelefon, Ihre IP-Nummer, Ihren geografischen Standort sowie die Seiten, die Sie anklicken (Interessen). Soweit Sie dem ausdrücklich zustimmen und die Informationen selbst eingeben, verarbeiten wir außerdem: Name, Telefonnummer, E-Mail, Adresse und Zahlungsinformationen. Dies geschieht in der Regel im Zusammenhang mit der Erstellung eines Logins oder bei einem Kauf.",
      },
      { type: "heading", text: "Sicherheit und Schutz" },
      {
        type: "paragraph",
        text: "Wir verarbeiten Ihre persönlichen Daten sicher und vertraulich in Übereinstimmung mit der geltenden Gesetzgebung, einschließlich der Datenschutz-Grundverordnung und des Datenschutzgesetzes.",
      },
      {
        type: "paragraph",
        text: "Ihre Daten werden nur für den Zweck verwendet, für den sie erhoben wurden, und werden gelöscht, wenn dieser Zweck erfüllt ist oder nicht mehr relevant ist.",
      },
      {
        type: "paragraph",
        text: "Wir haben technische und organisatorische Maßnahmen ergriffen, um zu verhindern, dass Ihre Daten versehentlich oder unrechtmäßig gelöscht, veröffentlicht, verloren, beeinträchtigt oder Unbefugten zur Kenntnis gebracht, missbraucht oder anderweitig unter Verstoß gegen die Gesetzgebung verarbeitet werden.",
      },
      { type: "heading", text: "Zweck" },
      {
        type: "paragraph",
        text: "Die Informationen werden verwendet, um Sie als Nutzer zu identifizieren und Ihnen die Werbung zu zeigen, die mit größter Wahrscheinlichkeit für Sie relevant ist, um Ihre Käufe und Zahlungen zu registrieren und um die von Ihnen angeforderten Dienste bereitzustellen, wie z.B. den Versand eines Newsletters. Darüber hinaus verwenden wir die Informationen, um unsere Dienste und Inhalte zu optimieren.",
      },
      { type: "heading", text: "Dauer der Speicherung" },
      {
        type: "paragraph",
        text: "Die Daten werden für den gesetzlich zulässigen Zeitraum gespeichert und wir löschen sie, wenn sie nicht mehr benötigt werden. Der Zeitraum hängt von der Art der Daten und dem Grund der Speicherung ab. Es ist daher nicht möglich, einen allgemeinen Zeitrahmen für die Löschung von Informationen anzugeben.",
      },
      { type: "heading", text: "Offenlegung von Informationen" },
      {
        type: "paragraph",
        text: 'Daten über Ihre Nutzung der Website, welche Anzeigen Sie erhalten und möglicherweise anklicken, geografischer Standort, Geschlecht und Alterssegment usw. werden an Dritte weitergegeben, soweit diese Informationen bekannt sind. Welche Dritten dies sind, können Sie im obigen Abschnitt über "Cookies" nachlesen. Die Informationen werden zur gezielten Ausrichtung von Werbung verwendet.',
      },
      {
        type: "paragraph",
        text: "Darüber hinaus nutzen wir eine Reihe von Dritten, um Daten zu speichern und zu verarbeiten. Diese verarbeiten Daten ausschließlich in unserem Auftrag und dürfen sie nicht für eigene Zwecke verwenden.",
      },
      {
        type: "paragraph",
        text: "Die Weitergabe von personenbezogenen Daten wie Name und E-Mail usw. erfolgt nur, wenn Sie dem zustimmen. Wir setzen nur Auftragsverarbeiter in der EU oder in Ländern ein, die Ihren Daten einen ausreichenden Schutz bieten können.",
      },
      { type: "heading", text: "Einblicke und Beschwerden" },
      {
        type: "paragraph",
        text: "Sie haben das Recht zu erfahren, welche personenbezogenen Daten wir über Sie in einem gängigen Format verarbeiten (Datenübertragbarkeit). Sie können der Verwendung Ihrer Daten außerdem jederzeit widersprechen. Sie können auch Ihre Einwilligung in die Verarbeitung Ihrer Daten zurückziehen. Wenn die über Sie verarbeiteten Daten unrichtig sind, haben Sie das Recht, sie berichtigen oder löschen zu lassen. Anfragen hierzu können gerichtet werden an: info@misupport.dk.",
      },
      {
        type: "paragraph",
        text: "Wenn Sie sich über die Verarbeitung Ihrer persönlichen Daten durch uns beschweren möchten, haben Sie auch die Möglichkeit, sich an die dänische Datenschutzbehörde (Datatilsynet) zu wenden.",
      },
      {
        type: "paragraph",
        text: "Wenn Sie nicht mehr möchten, dass wir Ihre personenbezogenen Daten verarbeiten, oder wenn Sie möchten, dass wir die Verarbeitung Ihrer personenbezogenen Daten einschränken, können Sie uns auch eine entsprechende Anfrage an die oben genannte E-Mail-Adresse senden.",
      },
      { type: "heading", text: "Herausgeber" },
      { type: "paragraph", text: "Diese Website ist Eigentum von und wird veröffentlicht von:" },
      {
        type: "list",
        items: [
          "MI Support A/S",
          "Korskildelund 4, 1.",
          "2670 Greve",
          "Telefon: +45 5555 12 12",
          "E-Mail: info@misupport.dk",
        ],
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Persondatapolitik (recruitment privacy policy)
// ---------------------------------------------------------------------------
export const persondataStrings: Record<Locale, LegalStrings> = {
  da: {
    meta: {
      title: "Persondatapolitik for ansøgere - MI Support",
      description:
        "Læs hvordan MI Support A/S behandler, opbevarer og sletter dine personoplysninger i forbindelse med rekruttering, samt hvilke rettigheder du har.",
    },
    title: "Persondatapolitik",
    intro:
      "I forbindelse med rekruttering hos MI Support A/S vil vi behandle dine personoplysninger som her anført. MI Support A/S er dataansvarlig med hensyn til de personoplysninger, der indsamles og behandles i forbindelse med din ansøgning.",
    blocks: [
      { type: "heading", text: "Introduktion" },
      {
        type: "paragraph",
        text: "Til vores ansøgere bruger vi rekrutteringssystemet Emply. Formålet med dette er at lette rekrutteringsprocessen for dig og MI Support A/S og gøre det muligt at foretage effektive vurderinger af dig som kandidat.",
      },
      {
        type: "paragraph",
        text: "I forbindelse med brugen af Emply trækker MI Support A/S personoplysninger fra kandidater og behandler dem som et led i vurderingen af deres kompetencer og kvalifikationer i forhold til relevante stillinger eller uopfordrede ansøgninger.",
      },
      { type: "paragraph", text: "Kontakt os hvis du har spørgsmål." },
      { type: "heading", text: "Behandling af personoplysninger" },
      {
        type: "paragraph",
        text: "Vi behandler de oplysninger, herunder personoplysninger, som er skrevet i din ansøgning og dit CV med henblik på at evaluere din ansøgning og dit CV. Grundlaget for vores behandling af dine personoplysninger er din anmodning om vores behandling af din ansøgning og CV forud for eventuel indgåelse af en ansættelse (databeskyttelsesforordningens artikel 6, stk. 1, litra B).",
      },
      {
        type: "paragraph",
        text: "Som en del af vores evaluering kan det være, at den pågældende stilling kræver, at du gennemfører en personlighedstest eller at vi indhenter referencer fra tidligere eller nuværende arbejdsgiver. Dette gøres kun hvis du giver samtykke til dette (jf. databeskyttelsesforordningens artikel 6, stk. 1, litra B). Resultater fra personlighedstests vil blive slettet, når evalueringsprocessen er afsluttet – medmindre du opnår ansættelse hos MI Support A/S.",
      },
      {
        type: "paragraph",
        text: "Vi anbefaler, at din ansøgning ikke indeholder personfølsomme oplysninger som fx cpr.nr., etnisk baggrund, seksualitet, religion, helbredsoplysninger etc.",
      },
      { type: "heading", text: "Opbevaring og sletning" },
      {
        type: "paragraph",
        text: "Hvis du bliver tilbudt en stilling hos MI Support A/S, vil din ansøgning og de personoplysninger, der indsamles i forbindelse med ansættelsen, blive en del af din personalemappe.",
      },
      {
        type: "paragraph",
        text: "Tilbydes du ikke en stilling hos MI Support A/S, opbevarer vi din ansøgning og eventuelle yderligere personoplysninger, der indsamles i forbindelse med rekrutteringsprocessen, i en periode på 6 måneder.",
      },
      {
        type: "paragraph",
        text: "Hvis du uploader en uopfordret ansøgning eller CV til vores CV-bank, vil dine personoplysninger blive gemt i en periode på 6 måneder.",
      },
      { type: "heading", text: "Modtagere" },
      {
        type: "paragraph",
        text: "MI Support A/S bruger rekrutteringssystemet Emply til administration af rekrutteringsprocessen. MI Support A/S har indgået en databehandleraftale med Emply. Emply og deres underleverandører af IT-tjenester kan derfor have adgang til personoplysninger om kandidater som en del af deres drift af tjenesten. Alle underleverandører er omfattet af databehandleraftaler for at sikre databeskyttelse i henhold til den standard, der gælder databeskyttelseslovgivningen. For underleverandører, der befinder sig i tredjeland, vil en sådan overførsel være beskyttet af EU-kommissionens standardbestemmelser.",
      },
      { type: "heading", text: "Dine rettigheder" },
      {
        type: "paragraph",
        text: "Du har altid ret til at få indsigt i de personoplysninger, som vi behandler om dig. Du har ret til at gøre indsigelse mod indsamlingen og den videre behandling af dine personoplysninger. Du har derudover ret til at bede om at behandlingen af dine personoplysninger begrænses, at dine personoplysninger berigtiges, hvis du finder det nødvendigt - eller at dine personoplysninger helt slettes.",
      },
      {
        type: "paragraph",
        text: "Under særlige omstændigheder kan du bede om at få en oversigt over dine personoplysninger og bede os om at overføre disse til en anden dataansvarlig.",
      },
      {
        type: "paragraph",
        text: "Du kan tilbagekalde samtykker, som du har afgivet i forbindelse med din ansøgning eller i forbindelse med en rekrutteringsproces. Tilbagekaldelse af samtykker, har indvirkning for den fremtidige behandling af personoplysninger. Ønsker du at tilbagekalde et samtykke, kan du kontakte os som beskrevet herunder.",
      },
      {
        type: "paragraph",
        text: "Du kan til enhver tid redigere eller slette dine profiloplysninger ved at logge ind i din Emply-konto.",
      },
      { type: "heading", text: "Kontakt" },
      {
        type: "paragraph",
        text: "Hvis du ønsker at udøve nogle af disse rettigheder eller hvis du har spørgsmål vedrørende behandlingen af dine personoplysninger, er du velkommen til at kontakte os på:",
      },
      {
        type: "list",
        items: [
          "MI Support A/S",
          "Korskildelund 4, 1.",
          "2670 Greve",
          "Telefon: +45 5555 12 12",
          "Email: info@misupport.dk",
        ],
      },
      {
        type: "paragraph",
        text: "Ønsker du at klage over behandlingen af dine personoplysninger, kan du kontakte os på ovenstående adresse, telefonnummer eller e-mail.",
      },
    ],
  },

  en: {
    meta: {
      title: "Data policy for applicants - MI Support",
      description:
        "Read how MI Support A/S processes, stores and deletes your personal data in connection with recruitment, and what rights you have.",
    },
    title: "Your personal data is safe with us",
    intro:
      "In connection with recruitment at MI Support A/S, we will process your personal data as stated here. MI Support A/S is the data controller with regard to the personal data collected and processed in connection with your application.",
    blocks: [
      { type: "heading", text: "Getting started" },
      {
        type: "paragraph",
        text: "For our applicants, we use the recruitment system Emply. The purpose of this is to facilitate the recruitment process for you and MI Support A/S and make it possible to make effective assessments of you as a candidate.",
      },
      {
        type: "paragraph",
        text: "In connection with the use of Emply, MI Support A/S collects personal data from candidates and processes it as part of the assessment of their skills and qualifications in relation to relevant positions or unsolicited applications.",
      },
      { type: "paragraph", text: "Contact us if you have any questions." },
      { type: "heading", text: "Processing of personal data" },
      {
        type: "paragraph",
        text: "We process the information, including personal data, provided in your application and CV for the purpose of evaluating your application and CV. The basis for our processing of your personal data is your request for our processing of your application and CV prior to the possible conclusion of an employment contract (Article 6(1)(B) of the General Data Protection Regulation).",
      },
      {
        type: "paragraph",
        text: "As part of our evaluation, the position in question may require you to complete a personality test or that we obtain references from former or current employers. This is only done if you consent to this (cf. Article 6(1)(B) of the General Data Protection Regulation). Results from personality tests will be deleted when the evaluation process is completed - unless you obtain employment with MI Support A/S.",
      },
      {
        type: "paragraph",
        text: "We recommend that your application does not contain sensitive information such as social security number, ethnic background, sexuality, religion, health information etc.",
      },
      { type: "heading", text: "Storage and deletion" },
      {
        type: "paragraph",
        text: "If you are offered a position at MI Support A/S, your application and the personal data collected in connection with the employment will become part of your personnel file.",
      },
      {
        type: "paragraph",
        text: "If you are not offered a position at MI Support A/S, we will store your application and any additional personal data collected in connection with the recruitment process for a period of 6 months.",
      },
      {
        type: "paragraph",
        text: "If you upload an unsolicited application or CV to our CV bank, your personal data will be stored for a period of 6 months.",
      },
      { type: "heading", text: "Recipients" },
      {
        type: "paragraph",
        text: "MI Support A/S uses the recruitment system Emply for administration of the recruitment process. MI Support A/S has entered into a data processing agreement with Emply. Emply and their subcontractors of IT services may therefore have access to personal data about candidates as part of their operation of the service. All subcontractors are covered by data processing agreements to ensure data protection according to the standard set by applicable data protection legislation. For subcontractors located in third countries, such transfer will be protected by the EU Commission's standard clauses.",
      },
      { type: "heading", text: "Your rights" },
      {
        type: "paragraph",
        text: "You always have the right to access the personal data that we process about you. You have the right to object to the collection and further processing of your personal data. You also have the right to request that the processing of your personal data is restricted, that your personal data is rectified if you deem it necessary - or that your personal data is completely deleted.",
      },
      {
        type: "paragraph",
        text: "In exceptional circumstances, you can request an overview of your personal data and ask us to transfer it to another controller.",
      },
      {
        type: "paragraph",
        text: "You can withdraw consent that you have given in connection with your application or in connection with a recruitment process. Withdrawal of consent has an impact on the future processing of personal data. If you wish to withdraw consent, you can contact us as described below.",
      },
      {
        type: "paragraph",
        text: "You can edit or delete your profile information at any time by logging into your Emply account.",
      },
      { type: "heading", text: "Contact us" },
      {
        type: "paragraph",
        text: "If you wish to exercise any of these rights or if you have any questions regarding the processing of your personal data, you are welcome to contact us at:",
      },
      {
        type: "list",
        items: [
          "MI Support A/S",
          "Korskildelund 4, 1.",
          "2670 Greve",
          "Phone: +45 5555 12 12",
          "Email: info@misupport.dk",
        ],
      },
      {
        type: "paragraph",
        text: "If you wish to complain about the processing of your personal data, you can contact us at the above address, phone number or e-mail.",
      },
    ],
  },

  de: {
    meta: {
      title: "Datenschutzbestimmungen - MI Support",
      description:
        "Lesen Sie, wie MI Support A/S Ihre personenbezogenen Daten im Zusammenhang mit der Rekrutierung verarbeitet, speichert und löscht und welche Rechte Sie haben.",
    },
    title: "Ihre persönlichen Daten sind bei uns sicher",
    intro:
      "Im Zusammenhang mit der Einstellung bei MI Support A/S werden Ihre personenbezogenen Daten wie hier angegeben verarbeitet. MI Support A/S ist der für die Datenverarbeitung Verantwortliche in Bezug auf die personenbezogenen Daten, die im Zusammenhang mit Ihrer Bewerbung erhoben und verarbeitet werden.",
    blocks: [
      { type: "heading", text: "Einführung" },
      {
        type: "paragraph",
        text: "Für unsere Bewerber verwenden wir das Rekrutierungssystem Emply. Dies dient dazu, den Einstellungsprozess für Sie und MI Support A/S zu vereinfachen und eine effektive Bewertung Ihrer Person als Bewerber zu ermöglichen.",
      },
      {
        type: "paragraph",
        text: "Im Zusammenhang mit der Nutzung von Emply erhebt MI Support A/S personenbezogene Daten von Kandidaten und verarbeitet diese im Rahmen der Bewertung ihrer Kompetenzen und Qualifikationen in Bezug auf relevante Positionen oder Initiativbewerbungen.",
      },
      { type: "paragraph", text: "Kontaktieren Sie uns, wenn Sie Fragen haben." },
      { type: "heading", text: "Verarbeitung von personenbezogenen Daten" },
      {
        type: "paragraph",
        text: "Wir verarbeiten die in Ihrer Bewerbung und Ihrem Lebenslauf angegebenen Informationen, einschließlich personenbezogener Daten, zum Zweck der Bewertung Ihrer Bewerbung und Ihres Lebenslaufs. Die Grundlage für die Verarbeitung Ihrer personenbezogenen Daten ist Ihr Wunsch, dass wir Ihre Bewerbung und Ihren Lebenslauf vor dem möglichen Abschluss eines Arbeitsvertrags verarbeiten (Artikel 6 Abs. 1 lit. B der Datenschutz-Grundverordnung).",
      },
      {
        type: "paragraph",
        text: "Als Teil unserer Bewertung kann es für die betreffende Stelle erforderlich sein, dass Sie einen Persönlichkeitstest ausfüllen oder dass wir Referenzen von früheren oder aktuellen Arbeitgebern einholen. Dies geschieht nur, wenn Sie dem zustimmen (vgl. Artikel 6 Abs. 1 lit. B der Datenschutz-Grundverordnung). Die Ergebnisse von Persönlichkeitstests werden gelöscht, wenn der Bewertungsprozess abgeschlossen ist - es sei denn, Sie erhalten eine Anstellung bei MI Support A/S.",
      },
      {
        type: "paragraph",
        text: "Wir empfehlen, dass Ihre Bewerbung keine sensiblen Informationen wie Sozialversicherungsnummer, ethnische Herkunft, Sexualität, Religion, Gesundheitsdaten usw. enthält.",
      },
      { type: "heading", text: "Speicherung und Löschung" },
      {
        type: "paragraph",
        text: "Wenn Ihnen eine Stelle bei MI Support A/S angeboten wird, werden Ihre Bewerbung und die im Zusammenhang mit der Beschäftigung erhobenen personenbezogenen Daten Teil Ihrer Personalakte.",
      },
      {
        type: "paragraph",
        text: "Wenn Ihnen keine Stelle bei MI Support A/S angeboten wird, werden wir Ihre Bewerbung und alle weiteren personenbezogenen Daten, die im Zusammenhang mit dem Einstellungsverfahren erhoben wurden, für einen Zeitraum von 6 Monaten speichern.",
      },
      {
        type: "paragraph",
        text: "Wenn Sie eine Initiativbewerbung oder einen Lebenslauf in unsere Lebenslaufbank hochladen, werden Ihre persönlichen Daten für einen Zeitraum von 6 Monaten gespeichert.",
      },
      { type: "heading", text: "Empfänger" },
      {
        type: "paragraph",
        text: "MI Support A/S verwendet das Rekrutierungssystem Emply für die Verwaltung des Rekrutierungsprozesses. MI Support A/S hat einen Datenverarbeitungsvertrag mit Emply abgeschlossen. Emply und seine Unterauftragnehmer für IT-Dienstleistungen können daher im Rahmen des Betriebs des Dienstes Zugang zu personenbezogenen Daten von Bewerbern haben. Alle Subunternehmer sind durch Datenverarbeitungsverträge abgesichert, um den Datenschutz gemäß dem Standard der geltenden Datenschutzgesetze zu gewährleisten. Bei Unterauftragnehmern in Drittländern wird eine solche Übermittlung durch die Standardklauseln der EU-Kommission geschützt.",
      },
      { type: "heading", text: "Ihre Rechte" },
      {
        type: "paragraph",
        text: "Sie haben jederzeit das Recht auf Zugang zu den personenbezogenen Daten, die wir über Sie verarbeiten. Sie haben das Recht, der Erhebung und Weiterverarbeitung Ihrer personenbezogenen Daten zu widersprechen. Sie haben auch das Recht zu verlangen, dass die Verarbeitung Ihrer personenbezogenen Daten eingeschränkt wird, dass Ihre personenbezogenen Daten berichtigt werden, wenn Sie dies für notwendig erachten - oder dass Ihre personenbezogenen Daten vollständig gelöscht werden.",
      },
      {
        type: "paragraph",
        text: "In Ausnahmefällen können Sie eine Übersicht über Ihre persönlichen Daten anfordern und uns bitten, diese an einen anderen Verantwortlichen zu übertragen.",
      },
      {
        type: "paragraph",
        text: "Sie können Ihre Zustimmung, die Sie im Zusammenhang mit Ihrer Bewerbung oder im Rahmen eines Einstellungsverfahrens erteilt haben, zurückziehen. Der Widerruf der Zustimmung hat Auswirkungen auf die zukünftige Verarbeitung personenbezogener Daten. Wenn Sie Ihre Einwilligung zurückziehen möchten, können Sie uns wie unten beschrieben kontaktieren.",
      },
      {
        type: "paragraph",
        text: "Sie können Ihre Profilinformationen jederzeit bearbeiten oder löschen, indem Sie sich bei Ihrem Emply-Konto anmelden.",
      },
      { type: "heading", text: "Kontakt" },
      {
        type: "paragraph",
        text: "Wenn Sie eines dieser Rechte ausüben möchten oder Fragen zur Verarbeitung Ihrer personenbezogenen Daten haben, können Sie uns gerne kontaktieren unter:",
      },
      {
        type: "list",
        items: [
          "MI Support A/S",
          "Korskildelund 4, 1.",
          "2670 Greve",
          "Telefon: +45 5555 12 12",
          "E-Mail: info@misupport.dk",
        ],
      },
      {
        type: "paragraph",
        text: "Wenn Sie sich über die Verarbeitung Ihrer persönlichen Daten beschweren möchten, können Sie sich unter der oben genannten Adresse, Telefonnummer oder E-Mail an uns wenden.",
      },
    ],
  },
};
